import { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { UnitsProvider } from './context/UnitsContext';
import { BottomNav } from './components/BottomNav';
import { Toaster } from './components/ui/sonner';
import PrefetchWarmer from './components/PrefetchWarmer';
import BiometricEnablePrompt from './components/BiometricEnablePrompt';
import { initAnalytics, trackPageView, setUserId, setUserPremiumStatus } from './lib/analytics';
import { initNative, isNative, appLifecycle, statusBar } from './lib/native';
import { NetworkStatus } from './components/NetworkStatus';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import NuovoPiano from './pages/NuovoPiano';

// Lazy load Feed for instant navigation (big component)
const Feed = lazy(() => import('./pages/Feed'));
const Coach = lazy(() => import('./pages/Coach'));

import NuovoPost from './pages/NuovoPost';
import Challenges from './pages/Challenges';
import ChallengeDetail from './pages/ChallengeDetail';
import JoinChallenge from './pages/JoinChallenge';
import Profilo from './pages/Profilo';
import Camminata from './pages/Camminata';
import Classifica from './pages/Classifica';
import Assistenza from './pages/Assistenza';
import InserisciPeso from './pages/InserisciPeso';
import StoricoPesi from './pages/StoricoPesi';
import RicettaDetail from './pages/RicettaDetail';
import MieRicette from './pages/MieRicette';
import ChefBot from './pages/ChefBot';
import MieiAllenamenti from './pages/MieiAllenamenti';
import Move from './pages/Move';
import MovePlayer from './pages/MovePlayer';
import MealPlan from './pages/MealPlan';
import ShoppingList from './pages/ShoppingList';
import AdminPanel from './pages/AdminPanel';
import Landing from './pages/Landing';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import PublicSupport from './pages/PublicSupport';
import SetPassword from './pages/SetPassword';
import EmailLogin from './pages/EmailLogin';
import PhoneLogin from './pages/PhoneLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicPost from './pages/PublicPost';
import Optout from './pages/Optout';
import Premium from './pages/Premium';
import PremiumSuccess from './pages/PremiumSuccess';
import UserProfile from './pages/UserProfile';
import SearchUsers from './pages/SearchUsers';
import BlockedUsers from './pages/BlockedUsers';
import DeleteAccount from './pages/DeleteAccount';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import GeoSettingsScreen from './pages/GeoSettingsScreen';
import Messages from './pages/Messages';
import Conversation from './pages/Conversation';
import NewConversation from './pages/NewConversation';
import Diagnostica from './pages/Diagnostica';

import './index.css';

// Logo URLs - Slimmy branding
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/jiv1y93x_logo.png';
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/q2sfm1zu_logo_white.png';

export { LOGO_DARK, LOGO_LIGHT };

// Check if device is mobile
const getIsMobile = () => {
  if (typeof window === 'undefined') return true;
  const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (mobileUserAgent) return true;
  return window.innerWidth <= 1024;
};

// Analytics page view tracker
function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();
  
  useEffect(() => {
    // Track page view on route change
    const pageTitles = {
      '/': 'Landing',
      '/app/dashboard': 'Dashboard',
      '/app/feed': 'Feed',
      '/app/challenges': 'Challenges',
      '/app/profile': 'Profile',
      '/app/camminata': 'Walk Tracker',
      '/app/peso': 'Weight Entry',
      '/app/storico-pesi': 'Weight History',
      '/app/ricette': 'My Recipes',
      '/app/allenamenti': 'My Workouts',
      '/app/premium': 'Premium',
      '/admin': 'Admin Panel',
    };
    
    const title = pageTitles[location.pathname] || location.pathname;
    trackPageView(location.pathname, title);
  }, [location.pathname]);
  
  useEffect(() => {
    // Set user properties when authenticated
    if (user?.user_id) {
      setUserId(user.user_id);
      setUserPremiumStatus(user.is_premium);
    }
  }, [user]);
  
  return null;
}

// Router-scoped listener: navigates with React Router (NO reload) when the
// native deep link dispatches slimmy-auth-success after saving the token.
function DeepLinkAuthNavigator() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail?.next && e.detail.next.startsWith('/') ? e.detail.next : '/app/dashboard';
      console.log('[DeepLink] SPA navigating to', next);
      navigate(next, { replace: true });
    };
    window.addEventListener('slimmy-auth-success', handler);
    return () => window.removeEventListener('slimmy-auth-success', handler);
  }, [navigate]);
  return null;
}

// Component to handle admin access for non-admin users
function AdminLogoutRedirect() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const doLogout = async () => {
      await logout();
      navigate('/app/login', { replace: true });
    };
    doLogout();
  }, [logout, navigate]);
  
  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-[#080D1A]">
      <div className="spinner"></div>
    </div>
  );
}

// Protected App Content (requires auth)
function ProtectedApp() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { isDark } = useTheme();
  const isMobile = getIsMobile();

  // Scroll restoration refs (must be before any conditional returns)
  const scrollContainerRef = useRef(null);
  const prevPathRef = useRef(location.pathname);
  const scrollSaveTimerRef = useRef(null);
  const isRestoringRef = useRef(false);

  useEffect(() => {
    if (isMobile) {
      document.documentElement.classList.add('mobile-app');

      // Best-effort portrait lock. `portrait-primary` excludes the
      // 180° upside-down variant too (relevant on iPad). The Screen
      // Orientation API is fully supported on Android and PWAs but
      // NOT on iOS Safari / iOS WebView — for iOS the definitive
      // lock has to be configured in the Natively dashboard.
      const tryLock = () => {
        try {
          // eslint-disable-next-line no-undef
          if (screen.orientation && screen.orientation.lock) {
            // eslint-disable-next-line no-undef
            screen.orientation
              .lock('portrait-primary')
              .catch(() =>
                // Fallback to plain 'portrait' on browsers that don't
                // accept the granular value.
                // eslint-disable-next-line no-undef
                screen.orientation.lock('portrait').catch(() => {})
              );
          }
        } catch {
          /* unsupported — covered by Natively native setting */
        }
      };
      tryLock();
      // Some platforms only expose the API after the first user
      // gesture. Re-attempt on the first interaction.
      const onFirstTouch = () => {
        tryLock();
        window.removeEventListener('touchstart', onFirstTouch);
        window.removeEventListener('click', onFirstTouch);
      };
      window.addEventListener('touchstart', onFirstTouch, { once: true });
      window.addEventListener('click', onFirstTouch, { once: true });

      return () => {
        window.removeEventListener('touchstart', onFirstTouch);
        window.removeEventListener('click', onFirstTouch);
      };
    } else {
      document.documentElement.classList.remove('mobile-app');
    }
  }, [isMobile]);

  // Native push deep-linking. When the user taps a push notification
  // we read `data.open` (set by the backend in send_push_to_user) and
  // navigate to the matching in-app section. Mirror of the same map
  // implemented in /public/firebase-messaging-sw.js for the PWA path.
  const navigate = useNavigate();
  useEffect(() => {
    if (!isNative) return; // PWA path is handled by the service worker
    let removeAction = null;
    let removeReceived = null;
    const DEEP_LINKS = {
      bug: '/app/assistenza',
      assistenza: '/app/assistenza',
      classifica: '/app/classifica',
    };
    (async () => {
      try {
        const { pushNotifications } = await import('./lib/native');
        // Tap → deep-link
        const handleAction = await pushNotifications.onActionPerformed((event) => {
          const data = event?.notification?.data || {};
          const key = (data.open || '').toString().toLowerCase();
          const target = DEEP_LINKS[key];
          if (target) navigate(target);
        });
        if (handleAction && typeof handleAction.remove === 'function') {
          removeAction = () => handleAction.remove();
        }
        // Received while app is open (foreground on native) → fan
        // out on the global `slimmy:push` bus so toasts, banners,
        // badges all refresh live without waiting for the user to
        // tap the OS notification.
        const handleReceived = await pushNotifications.onReceived((notification) => {
          const data = notification?.data || {};
          const merged = {
            ...data,
            title: data.title || notification?.title,
            body: data.body || notification?.body,
          };
          window.dispatchEvent(new CustomEvent('slimmy:push', { detail: merged }));
        });
        if (handleReceived && typeof handleReceived.remove === 'function') {
          removeReceived = () => handleReceived.remove();
        }
      } catch (e) {
        console.debug('[push] native listeners skipped:', e?.message);
      }
    })();
    return () => {
      try { removeAction && removeAction(); } catch (_) {}
      try { removeReceived && removeReceived(); } catch (_) {}
    };
  }, [navigate]);

  // PWA foreground push handler. When the app is OPEN in the browser,
  // FCM does NOT auto-display the system notification — it just hands
  // the payload to the page via `onMessage`. Without this listener,
  // admin replies (and any other push) silently disappear while the
  // user is in any non-Camminata page.
  //
  // We also listen for `postMessage` from the service worker because
  // on iOS PWA, and any time the tab is in background, FCM delivers
  // the push to the SW (`onBackgroundMessage`) instead of the page.
  // The SW relays the payload to its clients, and we re-dispatch it
  // on the global bus so toasts/banners/badges still update live.
  //
  // We:
  //   1. Display the notification ourselves via the active SW (so it
  //      looks identical to a background-delivered push).
  //   2. Fire a window event that interested components (e.g. the
  //      home support banner) can listen to for an immediate refresh
  //      instead of waiting up to 30 s for the next poll tick.
  useEffect(() => {
    if (isNative) return; // Native uses Capacitor plugin, handled separately
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    let unsub = () => {};
    let cancelled = false;

    // Bridge: SW → page. When the SW receives a background push it
    // posts a `slimmy:push` message; convert it into the same window
    // event the foreground handler dispatches so listeners don't
    // care about the delivery path.
    const onSwMessage = (event) => {
      if (event?.data?.type !== 'slimmy:push') return;
      // eslint-disable-next-line no-console
      console.log('[push-bus] SW relay received', event.data.data);
      window.dispatchEvent(new CustomEvent('slimmy:push', { detail: event.data.data || {} }));
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }

    (async () => {
      try {
        const { onForegroundMessage } = await import('./lib/firebase');
        unsub = await onForegroundMessage(async (payload) => {
          if (cancelled) return;
          const data = payload?.data || {};
          // eslint-disable-next-line no-console
          console.log('[push-bus] foreground onMessage', { type: data.type, title: data.title, body: data.body });
          // App is in foreground — only fire the in-app bus. The
          // toast component will handle the visible UI; suppressing
          // the OS notification here avoids the duplicate banner the
          // user reported (system banner + in-app toast at once).
          window.dispatchEvent(new CustomEvent('slimmy:push', { detail: data }));
        });
      } catch (_) { /* messaging not available — PWA push disabled */ }
    })();
    return () => {
      cancelled = true;
      try { unsub(); } catch (_) {}
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, []);

  // Continuously save scroll position (debounced) for current route
  useEffect(() => {
    const handleScroll = () => {
      if (isRestoringRef.current) return;
      clearTimeout(scrollSaveTimerRef.current);
      scrollSaveTimerRef.current = setTimeout(() => {
        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        const containerY = scrollContainerRef.current?.scrollTop || 0;
        const val = Math.max(scrollY, containerY);
        if (val > 0) {
          sessionStorage.setItem(`scroll_${location.pathname}`, String(val));
        } else {
          // User at top — clear old saved position so return starts at top
          sessionStorage.removeItem(`scroll_${location.pathname}`);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, true); // capture phase catches nested container scrolls
    return () => {
      clearTimeout(scrollSaveTimerRef.current);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [location.pathname]);

  // Restore scroll when route changes
  useEffect(() => {
    const prevPath = prevPathRef.current;
    if (prevPath !== location.pathname) {
      prevPathRef.current = location.pathname;

      const saved = sessionStorage.getItem(`scroll_${location.pathname}`);
      if (saved && parseInt(saved) > 0) {
        isRestoringRef.current = true;
        const scrollVal = parseInt(saved);
        
        const restore = () => {
          window.scrollTo(0, scrollVal);
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollVal;
          }
        };
        
        // Retry multiple times to beat async content loading
        setTimeout(restore, 50);
        setTimeout(restore, 200);
        setTimeout(restore, 500);
        setTimeout(() => { 
          restore(); 
          isRestoringRef.current = false; 
        }, 800);
      }
    }
  }, [location.pathname]);

  // Check URL for session_id during render (handles OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Show loading while checking auth — token present + authLoading MUST
  // never redirect to /app/login
  console.log('[AuthGuard] authLoading', loading);
  console.log('[AuthGuard] user present', !!user);
  if (loading) {
    return (
      <div className={`w-full min-h-screen flex items-center justify-center ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}>
        <div className="spinner"></div>
      </div>
    );
  }

  // Public join challenge page (accessible without auth)
  if (location.pathname.startsWith('/join/challenge/')) {
    return <JoinChallenge />;
  }

  // If not authenticated, redirect to login
  if (!user) {
    const returnUrl = location.pathname !== '/app' && location.pathname !== '/app/' 
      ? `?returnUrl=${encodeURIComponent(location.pathname)}` 
      : '';
    console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
    console.log('[AuthGuard] current path', location.pathname);
    console.log('[AuthGuard] redirect target', '/app/login (ProtectedRoute: no user)');
    return <Navigate to={`/app/login${returnUrl}`} replace />;
  }

  // Geo onboarding guard. Brand-new accounts (registered after the
  // phone-auth refactor on 2026-04-30) land with `weight_unit` ABSENT
  // — the deliberate trigger for the GeoSettingsScreen wizard. We
  // surface that wizard at the very root of the protected app so it
  // appears BEFORE the user can see "Crea Piano" or any other screen.
  // Existing users keep `weight_unit` set, so they bypass this guard
  // and continue straight to their intended page.
  //
  // GeoSettingsScreen calls `refreshUser` internally on Save before
  // firing `onComplete`, so by the time we navigate the AuthContext
  // already has the freshly-saved geo defaults — this guard auto-
  // releases on the next render without any extra hook.
  if (!user.weight_unit) {
    return <GeoSettingsScreen onComplete={() => navigate('/app/dashboard', { replace: true })} />;
  }

  // Pages without bottom nav
  const noNavPages = ['/app/nuovo-piano', '/app/nuovo-post', '/app/login', '/app/assistenza', '/app/move/player', '/app/coach'];
  const isChatPage =
    /^\/app\/messaggi\/[^/]+/.test(location.pathname) &&
    location.pathname !== '/app/messaggi';
  const showNav = !isChatPage && !noNavPages.some((p) => location.pathname.startsWith(p));

  return (
    <>
      {/* Push Notification Prompt */}
      <PushNotificationPrompt />

      {/* Warm React Query cache with frequently-accessed data for instant navigation */}
      <PrefetchWarmer />
      {/* One-shot biometric onboarding (only on native, only first
          authenticated session, only if device supports it). */}
      <BiometricEnablePrompt />
      
      {/* Main content area */}
      <div ref={scrollContainerRef}
           className={`h-screen overflow-y-auto overflow-x-hidden ${showNav ? 'safe-area-bottom' : ''}`}
           style={{ WebkitOverflowScrolling: 'touch' }}>
        <Routes>
          <Route path="/" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="coach" element={<Coach />} />
          <Route path="nuovo-piano" element={<NuovoPiano />} />
          <Route path="feed" element={
            <Suspense fallback={<div className="feed-loading-placeholder" />}>
              <Feed />
            </Suspense>
          } />
          <Route path="nuovo-post" element={<NuovoPost />} />
          <Route path="sfide" element={<Challenges />} />
          <Route path="challenges" element={<Challenges />} />
          <Route path="challenge/:id" element={<ChallengeDetail />} />
          <Route path="profilo" element={<Profilo />} />
          <Route path="classifica" element={<Classifica />} />
          <Route path="assistenza" element={<Assistenza />} />
          <Route path="camminata" element={<Camminata />} />
          <Route path="inserisci-peso" element={<InserisciPeso />} />
          <Route path="storico-pesi" element={<StoricoPesi />} />
          <Route path="ricetta" element={<RicettaDetail />} />
          <Route path="mie-ricette" element={<MieRicette />} />
          <Route path="chef" element={<ChefBot />} />
          <Route path="chef-bot" element={<ChefBot />} />
          <Route path="miei-allenamenti" element={<MieiAllenamenti />} />
          <Route path="move" element={<Move />} />
          <Route path="move/player" element={<MovePlayer />} />
          <Route path="piano-alimentare" element={<MealPlan />} />
          <Route path="lista-spesa" element={<ShoppingList />} />
          <Route path="premium" element={<Premium />} />
          <Route path="premium/success" element={<PremiumSuccess />} />
          <Route path="utente/:userId" element={<UserProfile />} />
          <Route path="cerca-utenti" element={<SearchUsers />} />
          <Route path="utenti-bloccati" element={<BlockedUsers />} />
          <Route path="elimina-account" element={<DeleteAccount />} />
          <Route path="messaggi" element={<Messages />} />
          <Route path="messaggi/nuova/:userId" element={<NewConversation />} />
          <Route path="messaggi/:id" element={<Conversation />} />
          <Route path="diagnostica" element={<Diagnostica />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </div>
      
      {/* BottomNav */}
      {showNav && <BottomNav />}
    </>
  );
}

// Main App Router
function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { isDark } = useTheme();

  // Signal the splash overlay (in App.js + index.html) that real
  // content is about to paint. Fires once, the moment auth check is
  // done. The splash listens for `slimmy:app-ready` and starts its
  // fade-out — so on slow networks the user keeps seeing the splash
  // (not a black-screen-with-spinner) until everything is ready.
  useEffect(() => {
    if (loading) return;
    // Wait for the NEXT paint frame before signaling, so the route
    // below the splash has had a chance to render its first content.
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('slimmy:app-ready'));
      });
    });
    return () => cancelAnimationFrame(raf1);
  }, [loading]);

  // Check if running as installed PWA (standalone mode)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;

  // If native app or PWA opened at root, redirect to /app
  if ((isNative || isStandalone) && (location.pathname === '/' || location.pathname === '')) {
    return <Navigate to="/app" replace />;
  }

  // Landing page - accessible to everyone at root (only in browser mode)
  if (location.pathname === '/' || location.pathname === '') {
    document.documentElement.classList.remove('mobile-app');
    return <Landing />;
  }

  // Privacy and Terms pages - accessible to everyone
  if (location.pathname === '/privacy') {
    document.documentElement.classList.remove('mobile-app');
    return <Privacy />;
  }
  if (location.pathname === '/terms') {
    document.documentElement.classList.remove('mobile-app');
    return <Terms />;
  }
  if (location.pathname === '/cookies') {
    document.documentElement.classList.remove('mobile-app');
    return <Cookies />;
  }

  // Public assistance form (from Landing footer) - no auth required
  if (location.pathname === '/support-request') {
    document.documentElement.classList.remove('mobile-app');
    return <PublicSupport />;
  }

  // Public post page - accessible to everyone (for WhatsApp sharing)
  if (location.pathname.startsWith('/post/')) {
    document.documentElement.classList.remove('mobile-app');
    return <PublicPost />;
  }

  // Public account-delete (opt-out) page — slimmy.app/optout. Allows
  // anyone to authenticate via username+password and delete their
  // own Slimmy account directly from the browser, no app required.
  if (location.pathname === '/optout' || location.pathname.startsWith('/optout')) {
    document.documentElement.classList.remove('mobile-app');
    return <Optout />;
  }

  // Admin panel
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin')) {
    // Check for pending admin redirect
    const pendingAdminRedirect = localStorage.getItem('pending_admin_redirect');
    if (pendingAdminRedirect) {
      localStorage.removeItem('pending_admin_redirect');
    }
    
    if (user && !user.is_admin) {
      return <AdminLogoutRedirect />;
    }
    if (!loading && user?.is_admin) {
      return (
        <div className={`w-full min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-gray-50'}`}>
          <AdminPanel />
        </div>
      );
    }
    // If still loading, show spinner
    if (loading) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-[#080D1A]">
          <div className="spinner"></div>
        </div>
      );
    }
  }

  // Auth pages under /app
  if (location.pathname === '/app/login') {
    document.documentElement.classList.remove('mobile-app');
    console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
    console.log('[AuthGuard] current path', location.pathname);
    if (user) {
      const searchParams = new URLSearchParams(location.search);
      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl && returnUrl.startsWith('/')) {
        console.log('[AuthGuard] redirect target', returnUrl);
        return <Navigate to={returnUrl} replace />;
      }
      console.log('[AuthGuard] redirect target', '/app/dashboard (user authenticated)');
      return <Navigate to="/app/dashboard" replace />;
    }
    console.log('[AuthGuard] redirect target', 'none (rendering login page)');
    return <PhoneLogin />;
  }

  if (location.pathname === '/app/accedi') {
    document.documentElement.classList.remove('mobile-app');
    console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
    console.log('[AuthGuard] current path', location.pathname);
    if (user) {
      console.log('[AuthGuard] redirect target', '/app/dashboard (user authenticated)');
      return <Navigate to="/app/dashboard" replace />;
    }
    console.log('[AuthGuard] redirect target', 'none (rendering email login page)');
    return <EmailLogin />;
  }

  if (location.pathname === '/app/registrati') {
    document.documentElement.classList.remove('mobile-app');
    console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
    console.log('[AuthGuard] current path', location.pathname);
    if (user) {
      console.log('[AuthGuard] redirect target', '/app/dashboard (user authenticated)');
      return <Navigate to="/app/dashboard" replace />;
    }
    console.log('[AuthGuard] redirect target', 'none (rendering register page)');
    return <Login />;
  }

  // Password reset pages
  if (location.pathname === '/set-password') {
    document.documentElement.classList.remove('mobile-app');
    return <SetPassword />;
  }
  if (location.pathname === '/forgot-password') {
    document.documentElement.classList.remove('mobile-app');
    return <ForgotPassword />;
  }
  if (location.pathname === '/reset-password') {
    document.documentElement.classList.remove('mobile-app');
    return <ResetPassword />;
  }

  // Legacy redirects
  if (location.pathname === '/login') {
    return <Navigate to="/app/login" replace />;
  }
  if (location.pathname === '/registrati') {
    return <Navigate to="/app/registrati" replace />;
  }
  if (location.pathname === '/dashboard') {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (location.pathname === '/desktop') {
    return <Navigate to="/" replace />;
  }

  // Handle /app exact path - redirect to login or dashboard based on auth
  if (location.pathname === '/app' || location.pathname === '/app/') {
    if (loading) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-[#080D1A]">
          <div className="spinner"></div>
        </div>
      );
    }
    if (user) {
      console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
      console.log('[AuthGuard] current path', location.pathname);
      console.log('[AuthGuard] redirect target', '/app/dashboard (/app root, authenticated)');
      return <Navigate to="/app/dashboard" replace />;
    }
    console.log('[AuthGuard] session_token present', !!localStorage.getItem('session_token'));
    console.log('[AuthGuard] current path', location.pathname);
    console.log('[AuthGuard] redirect target', '/app/login (/app root, no user)');
    return <Navigate to="/app/login" replace />;
  }

  // Protected app routes
  if (location.pathname.startsWith('/app')) {
    return (
      <div className={`w-full min-h-screen flex justify-center ${isDark ? 'bg-black' : 'bg-gray-100'}`}>
        <div className={`w-full max-w-md min-h-screen relative overflow-hidden ${
          isDark 
            ? 'bg-[#080D1A] shadow-2xl shadow-[#E8334A]/10' 
            : 'bg-white shadow-xl'
        }`}>
          <Routes>
            <Route path="/app/*" element={<ProtectedApp />} />
          </Routes>
          {/* PushToast intentionally NOT mounted: the product owner
              prefers persistent home banners (SupportReplyBanner +
              InteractionsBanner) over volatile 3.5-s toasts that can
              be missed. Banners are still updated live via the same
              `slimmy:push` bus that previously fed the toast. */}
        </div>
      </div>
    );
  }

  // Default redirect to landing
  return <Navigate to="/" replace />;
}

function App() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Initialize Google Analytics
    initAnalytics();
    // Initialize native platform features (Capacitor)
    initNative();

    // Sync admin's splash_enabled flag into localStorage.
    // The inline <script> in index.html reads `_splash_disabled` SYNCHRONOUSLY
    // at page boot (before any fetch can complete), so we cache the admin's
    // choice here — it takes effect from the NEXT app open.
    // eslint-disable-next-line no-undef
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API_BASE}/api/settings/public`)
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        if (!cfg) return;
        try {
          if (cfg.splash_enabled === false) {
            localStorage.setItem('_splash_disabled', '1');
          } else {
            localStorage.removeItem('_splash_disabled');
          }
        } catch (e) { /* noop */ }
      })
      .catch(() => { /* network error — keep current cached value */ });

    // Hide both splash screens (native Capacitor + inline HTML) in sync,
    // once the FIRST ROUTE has actually painted real content. We listen
    // to a `slimmy:app-ready` custom event dispatched by the first
    // route's useEffect (Dashboard or Login). This guarantees the splash
    // stays visible during:
    //   • React bundle download
    //   • React mount
    //   • auth/me fetch
    //   • initial route mount + first paint
    // Without this, on slow networks the user briefly sees the loading
    // spinner of the auth check between the splash fade-out and the
    // dashboard rendering.
    const hideSplash = () => {
      if (window.__splashHidden) return;
      window.__splashHidden = true;
      const inlineSplash = document.getElementById('splash-screen');
      if (inlineSplash) {
        // Two-layer fade (see index.html CSS):
        //  - ::before (image) fades 0 → 350ms
        //  - container bg-color fades 350 → 700ms (via transition-delay)
        // Remove from DOM at 750ms, after both fades are fully done.
        inlineSplash.classList.add('hiding');
        setTimeout(() => inlineSplash.remove(), 750);
      }
      // Hide the native iOS/Android Capacitor splash (LaunchScreen).
      // `launchAutoHide` is false in capacitor.config.ts so the OS keeps
      // it up until we call this — preventing the white-flash that
      // happened before the React bundle was ready.
      import('./lib/native').then(({ splash }) => {
        splash.hide().catch(() => {});
      }).catch(() => {});
      // Mark this session so any WebView reload (e.g. return from external
      // payment redirect like Amazon Pay / Google Pay) doesn't re-show splash.
      try { sessionStorage.setItem('_splash_done', '1'); } catch (e) { /* noop */ }
    };

    const MIN_SPLASH_MS = 800;
    const startTs = performance.now();
    const triggerFadeWhenReady = () => {
      const elapsed = performance.now() - startTs;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      setTimeout(() => requestAnimationFrame(hideSplash), wait);
    };

    // Primary trigger: the first route fires this when its real content
    // is on screen. Far more reliable than window.load on slow networks.
    const onAppReady = () => triggerFadeWhenReady();
    window.addEventListener('slimmy:app-ready', onAppReady, { once: true });

    // Safety net: hide after 6s no matter what so the user is never
    // stuck staring at the splash if something goes wrong.
    const safetyTimer = setTimeout(triggerFadeWhenReady, 6000);

    return () => {
      window.removeEventListener('slimmy:app-ready', onAppReady);
      clearTimeout(safetyTimer);
    };
  }, []);

  if (!mounted) return null;

  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <UnitsProvider>
            <BrowserRouter>
              <NetworkStatus />
              <AnalyticsTracker />
              <DeepLinkAuthNavigator />
              <Toaster position="top-center" richColors toastOptions={{ style: { marginTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' } }} />
              <AppRouter />
            </BrowserRouter>
          </UnitsProvider>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
